// Section 24.4: the season runner. Executes phases in order with ask-then-resume pausing
// (ADR-0007). Each phase runs in its own Immer draft; a pausing phase's draft is discarded,
// the prompt and the phase id are written to state, and the rng position stays at the start
// of that phase so a resumed season consumes the same random numbers (24.4 rule 4).
import { createDraft, enableArrayMethods, finishDraft, freeze } from 'immer';
import type { Config } from '../config';
import { SimError } from './errors';
import { assertInvariants } from './invariants';
import type { Registry } from './registry';
import { createExpectedValueRng, createRng, type Rng, type RngHandle } from './rng';
import { roundState } from './round';
import { trace } from './trace';
import type { GameState, Phase, PhaseContext, ReportBuilder, SeasonInfo, SimMode } from './types';

// Section 2 budget (M6): Immer's array-methods plugin runs filter/find/some/... on a draft array
// without drafting every element it visits (the results are still drafts). Behaviour is
// unchanged; ADR-0017.
enableArrayMethods();

export interface RunOptions {
  cfg: Config;
  registry: Registry;
  phases: readonly Phase[];
  mode: SimMode;
  /** Assert invariants after every phase (dev and test builds). */
  checkInvariants: boolean;
}

export function seasonInfo(cfg: Config, state: GameState): SeasonInfo {
  const seasonIndex = cfg.seasons.order.indexOf(state.season);
  return {
    season: state.season,
    year: state.year,
    turn: state.turn,
    seasonIndex,
    isYearStart: seasonIndex === 0,
    isYearEnd: seasonIndex === cfg.seasons.order.length - 1,
  };
}

function reportBuilder(draft: GameState): ReportBuilder {
  return {
    add(entry) {
      const report = draft.currentReport;
      if (report === null) throw new SimError('invariant', 'report entry added outside a season');
      report.entries.push(entry);
    },
    get current() {
      const report = draft.currentReport;
      if (report === null) throw new SimError('invariant', 'no season report is open');
      return report;
    },
  };
}

function streams(
  state: GameState,
  mode: SimMode,
): { main: RngHandle | null; events: RngHandle | null; rng: Rng; eventRng: Rng } {
  if (mode === 'projection') {
    const ev = createExpectedValueRng();
    return { main: null, events: null, rng: ev, eventRng: ev };
  }
  const main = createRng(state.rngState);
  const events = createRng(state.eventRngState);
  return { main, events, rng: main.rng, eventRng: events.rng };
}

/**
 * Run the season from `state.phaseCursor` (or the first phase) to the end, or until a phase
 * pauses. Returns the new state; a paused state has `phaseCursor` and `pendingPrompts` set.
 */
export function runSeason(state: GameState, opts: RunOptions): GameState {
  const { phases, cfg, registry, mode } = opts;
  let start = 0;
  if (state.phaseCursor !== null) {
    start = phases.findIndex((p) => p.id === state.phaseCursor);
    if (start < 0)
      throw new SimError('invariant', `unknown phase cursor ${state.phaseCursor}`, {
        turn: state.turn,
      });
  }
  let current = state;
  for (let i = start; i < phases.length; i++) {
    const phase = phases[i] as Phase;
    if (mode === 'projection' && phase.projection === 'skip') continue;
    trace('phase:start', { id: phase.id, turn: current.turn });
    const draft = createDraft(current);
    const s = streams(current, mode);
    const season = seasonInfo(cfg, current);
    const ctx: PhaseContext = {
      state: current,
      draft,
      live: { state: draft, cfg, registry, season, mode },
      cfg,
      registry,
      season,
      mode,
      rng: s.rng,
      eventRng: s.eventRng,
      report: reportBuilder(draft),
      answers: current.promptAnswers,
    };
    const result = phase.run(ctx);
    if (result !== 'done') {
      // Discard the draft: a pausing phase must not have mutated anything (ADR-0007).
      const pausedDraft = createDraft(current);
      pausedDraft.pendingPrompts = [result.paused];
      pausedDraft.phaseCursor = phase.id;
      const paused = finishDraft(pausedDraft);
      trace('phase:paused', { id: phase.id, prompt: result.paused.id });
      if (opts.checkInvariants) assertInvariants(paused, { cfg, registry, phase: phase.id });
      return paused;
    }
    if (s.main !== null) draft.rngState = s.main.state();
    if (s.events !== null) draft.eventRngState = s.events.state();
    if (draft.phaseCursor !== null) {
      draft.phaseCursor = null;
      draft.pendingPrompts = [];
    }
    const next = finishDraft(draft);
    if (opts.checkInvariants)
      assertInvariants(next, { cfg, registry, previous: current, phase: phase.id });
    trace('phase:end', { id: phase.id, turn: next.turn });
    current = next;
  }
  // Committed states are deep-frozen when Immer auto-freezes (dev and tests, to catch mutation);
  // production builds turn auto-freeze off for the Section 2 budget (ADR-0010 item 27), so the
  // rounded state is not frozen there either. `current` is a finished draft (the rng state
  // always changes), so it is frozen exactly when auto-freeze is on.
  const rounded = roundState(current);
  return Object.isFrozen(current) ? freeze(rounded, true) : rounded;
}
